import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import axios from "axios";

import { API_BASE as API } from "@/config/api";

export default function ProductCard({ product, className = "" }) {
  const [wishlist, setWishlist] = useState(false);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    setAdding(true);
    try { await addToCart(product.id, 1); }
    catch (err) { console.error(err); }
    finally { setAdding(false); }
  };

  const handleWishlist = async (e) => {
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    try {
      if (wishlist) {
        await axios.delete(`${API}/wishlist/${product.id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/wishlist/${product.id}`, {}, { withCredentials: true });
      }
      setWishlist(!wishlist);
    } catch (err) { console.error(err); }
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`polaroid-card group relative ${className}`}
      onClick={() => navigate(`/shop/${product.id}`)}
    >
      {/* Badges */}
      <div className="absolute top-4 left-4 flex flex-col gap-1 z-10">
        {product.is_new && (
          <span className="bg-petal-olive text-white text-xs px-2 py-0.5 font-outfit uppercase tracking-wider">New</span>
        )}
        {product.is_bestseller && (
          <span className="bg-petal-gold text-white text-xs px-2 py-0.5 font-outfit uppercase tracking-wider">Bestseller</span>
        )}
        {discount > 0 && (
          <span className="bg-red-400 text-white text-xs px-2 py-0.5 font-outfit">{discount}% off</span>
        )}
      </div>

      {/* Wishlist btn */}
      <button
        data-testid={`wishlist-btn-${product.id}`}
        onClick={handleWishlist}
        className="absolute top-4 right-4 z-10 w-8 h-8 bg-white/90 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Heart className={`w-4 h-4 ${wishlist ? "fill-red-400 text-red-400" : "text-petal-muted"}`} />
      </button>

      {/* Image */}
      <div className="overflow-hidden aspect-square mb-3">
        <img
          src={product.images?.[0] || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&q=80"}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>

      {/* Info */}
      <div className="px-1">
        <p className="font-outfit text-xs text-petal-muted uppercase tracking-widest mb-1">{product.category}</p>
        <h3 className="font-playfair text-base text-petal-olive-text line-clamp-1 mb-2">{product.name}</h3>

        <div className="flex items-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-3 h-3 ${i < Math.floor(product.rating || 0) ? "fill-petal-gold text-petal-gold" : "text-petal-border"}`} />
          ))}
          <span className="font-outfit text-xs text-petal-muted ml-1">({product.review_count || 0})</span>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <span className="font-outfit text-lg font-semibold text-petal-olive-text">₹{product.price.toLocaleString()}</span>
            {product.original_price && (
              <span className="font-outfit text-xs text-petal-muted line-through ml-2">₹{product.original_price.toLocaleString()}</span>
            )}
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAddToCart}
            disabled={adding}
            className="w-8 h-8 bg-petal-olive text-white flex items-center justify-center hover:bg-petal-olive-dark transition-colors disabled:opacity-50"
          >
            <ShoppingBag className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
